import type { JSX } from 'react';
import { CheckCircle2, Circle, Zap } from 'lucide-react';

import type { Project } from '~/types';

/**
 * Status → icon/color mapping shared by every card that renders a project's
 * status, extracted verbatim (same cases, same Tailwind classes, same
 * default fallback) from `ProjectCard.tsx`'s original inline
 * `getStatusIcon`/`getStatusColor` — the codebase's mockup-confirmed
 * canonical status treatment.
 *
 * `status` is typed as `Project['status']` (which resolves to `string`,
 * matching the pre-extraction signature) rather than the `ProjectStatus`
 * enum — the codebase does not type this field as the enum at the model
 * boundary, so this extraction does not narrow or widen it.
 */

export function getProjectStatusColor(status: Project['status']): string {
  switch (status) {
    case 'SCOPING':
    case 'TASKING':
    case 'TODO':
      return 'bg-foreground-50 dark:bg-foreground-100 text-foreground-700 dark:text-foreground-600';
    case 'IN_PROGRESS':
      return 'bg-primary-100 dark:bg-primary-200 text-primary-700 dark:text-primary-800';
    case 'DONE':
      return 'bg-success-100 dark:bg-success-200 text-success-700 dark:text-success-800';
    default:
      return 'bg-foreground-50 dark:bg-foreground-100 text-foreground-500 dark:text-foreground-600';
  }
}

export function getProjectStatusIcon(status: Project['status']): JSX.Element {
  switch (status) {
    case 'SCOPING':
    case 'TASKING':
    case 'TODO':
      return <Circle className="w-4 h-4 text-foreground-600 shrink-0" />;
    case 'IN_PROGRESS':
      return <Zap className="w-4 h-4 text-primary shrink-0" />;
    case 'DONE':
      return <CheckCircle2 className="w-4 h-4 text-success shrink-0" />;
    default:
      return <Circle className="w-4 h-4 text-foreground-500 shrink-0" />;
  }
}
